import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { CatalogosApi } from "../../hooks/catalogos";
import { configuracoesApi } from "../../hooks/configuracoesApi";
import { EmpresaApi } from "../../hooks/empresaApi";
import { listasDePrecoApi } from "../../hooks/listasDePreco";
import { produtosApi } from "../../hooks/produtosApi";
import { AjusteCategoria, AjusteVariacao, CatalogoUnico, DescontoProgressivo } from "../../types/Catalogo";
import { Intermediadores } from "../../types/Configuracoes";
import ListaDePreco from "../../types/ListaDePreco";
import { Categoria } from "../../types/Categoria";
import { CatalogoContext } from "./CatalogoContext";

interface CatalogoProviderProps {
  children: JSX.Element;
}

export const CatalogoProvider: React.FC<CatalogoProviderProps> = ({
  children,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const listaDePrecosApi = listasDePrecoApi();
  const [listasDePrecos, setListasDePrecos] = useState<ListaDePreco[]>([]);

  useEffect(() => {
    //forcar um update na pagina quando o catalogo for duplicados
    if (
      location.pathname.includes("duplicado") &&
      !location.pathname.includes("#avancado")
    ) {
      init();
    }
  }, [location]);

  const [loading, setLoading] = useState(false);
  const catalogoApi = CatalogosApi();
  const empresaApi = EmpresaApi();
  const configApi = configuracoesApi();

  const { id } = useParams<{ id: string }>();
  let { duplicated } = useParams<{ duplicated: string }>();

  if (duplicated != "duplicado") {
    duplicated = "";
  }

  const [outrosUsuarios, setOutrosUsuarios] = useState<
    { email: string; id: string }[]
  >([]);
  const [empresaIntermediadores, setEmpresaIntermediadores] =
    useState<Intermediadores>();
  const [descontosProgressivos, setDescontosProgressivos] = useState<DescontoProgressivo[]>([]);
  const [ajustesCategoria, setAjustesCategoria] = useState<AjusteCategoria[]>([]);
  const [ajustesVariacao, setAjustesVariacao] = useState<AjusteVariacao[]>([]);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const produtosApiInstance = produtosApi();

  const [catalogo, setCatalogo] = useState<CatalogoUnico>({
    id: id || "0",
    id_empresa: "",
    id_tabela: "",
    id_usuario: "",
    id_catalogo_original: "",
    data_vencimento: "2100-01-01 00:00",
    data_fim_manutencao: undefined,
    indice: "1",
    descricao: "",
    banners: [],
    // Mínimo de Compra
    minimo_valor: "",
    minimo_quantidade: "",
    minimo_ativo: false,
    // Ajuste de Preço
    ajuste_preco_ativo: false,
    ajuste_preco_escopo: 'catalogo',
    ajuste_preco_tipo: 'porcentagem',
    ajuste_preco_direcao: 'diminuir',
    ajuste_preco_valor: '',
    // Desconto Progressivo
    desconto_progressivo_ativo: false,
    desconto_progressivo_tipo: 'quantidade',
    intermediadores: {},
    identificacao: "",
    configuracoes: {
      adicionarTodosProdutos: false,
      mostrarPrecoOriginal: false,
      mostrarTagDesconto: false,
    },
    produtos: [],
    principal: "false",
    outros_usuarios: [],
    duplicado: false,
  });

  const init = async () => {
    setLoading(true);

    const [catalogoResponse, outrosUsuariosData, intermediadores, categoriasData] =
      await Promise.all([
        catalogo.id != "0" ? catalogoApi.getCatalogo(catalogo.id) : { catalogo, descontos_progressivos: [], ajustes_categoria: [], ajustes_variacao: [] },
        catalogoApi.getOutrosUsuarios(catalogo.id),
        empresaApi.getIntermediadores(),
        produtosApiInstance.getCategorias(),
      ]);

    // Extrair catalogo, descontos_progressivos, ajustes_categoria e ajustes_variacao do response
    let catalogoData = catalogoResponse?.catalogo || catalogoResponse;
    const descontosData = catalogoResponse?.descontos_progressivos || [];
    const ajustesCategoriaData = catalogoResponse?.ajustes_categoria || [];
    const ajustesVariacaoData = catalogoResponse?.ajustes_variacao || [];

    // Carregar categorias da empresa
    const ordered: any[] = [];
    if (categoriasData && Array.isArray(categoriasData)) {
      const pais = categoriasData.filter((c: any) => Number(c.id_pasta_pai) === 0).sort((a: any, b: any) => a.descricao.localeCompare(b.descricao));

      for (const pai of pais) {
        const subs = categoriasData.filter((c: any) => String(c.id_pasta_pai) === String(pai.id)).sort((a: any, b: any) => a.descricao.localeCompare(b.descricao));
        const subsTotal = subs.reduce((sum: number, s: any) => sum + Number(s.qtd_produtos || 0), 0);
        ordered.push({ ...pai, qtd_produtos: Number(pai.qtd_produtos || 0) + subsTotal });

        for (const sub of subs) {
          ordered.push({ ...sub, descricao: `${pai.descricao} / ${sub.descricao}` });
        }
      }

      setCategorias(ordered);
    }

    const [listaDePrecosData] = await Promise.all([
      listaDePrecosApi.getListas(true),
    ]);

    setListasDePrecos(
      listaDePrecosData.map((lista) => {
        return {
          ...lista,
          value: lista.id,
          name: lista.descricao,
        };
      })
    );
    if (catalogo.id === "0") {
      setCatalogo({
        ...catalogo,
        id_tabela: listaDePrecosData[0].id,
      });
    }

    // Carregar promocoes do backend (todas vem como ativas). Isso vale inclusive ao
    // duplicar: assim o desconto progressivo, os ajustes por categoria e por produto sao
    // replicados no novo catalogo (o ajuste geral e o minimo de compra ja vao junto nos
    // campos do proprio catalogo). Ao duplicar, o `id` de cada promocao e removido — sao
    // registros novos de um catalogo novo; o backend cria tudo do zero atrelado ao id novo.
    setDescontosProgressivos(descontosData.map((d: DescontoProgressivo) => ({
      ...d,
      id: duplicated ? undefined : d.id,
      ativo: true,
    })));
    // nome_categoria e qtd_produtos sao enriquecidos a partir de `ordered`, que ja tem
    // a hierarquia "Pai / Sub" e a cascata de contagem de subcategorias.
    setAjustesCategoria(ajustesCategoriaData.map((a: AjusteCategoria) => {
      const cat = ordered.find((c: any) => String(c.id) === String(a.id_categoria));
      return {
        ...a,
        id: duplicated ? undefined : a.id,
        ativo: true,
        nome_categoria: cat?.descricao ?? a.nome_categoria,
        qtd_produtos: cat ? Number(cat.qtd_produtos) : a.qtd_produtos,
      };
    }));
    setAjustesVariacao(ajustesVariacaoData.map((a: AjusteVariacao) => ({
      ...a,
      id: duplicated ? undefined : a.id,
      ativo: true,
    })));

    // Se for duplicado, ajustar apenas os metadados do catalogo (as promocoes acima
    // ja foram carregadas do original e serao recriadas no novo catalogo ao salvar).
    if (duplicated && catalogoData) {
      catalogoData.id = "";
      catalogoData.identificacao = "";
      catalogoData.descricao = catalogoData.descricao + " - Cópia";
      catalogoData.outros_usuarios = [];
      catalogoData.principal = "false";
      catalogoData.duplicado = true;
      delete catalogoData.id_catalogo_original;
    }

    const isPrincipal = catalogoData?.principal === "true" || String(catalogoData?.principal) === "true";
    if (catalogoData && isPrincipal) {
      try {
        const verificacao = await configApi.verificar_mudou_nome_catalogo();
        if (!verificacao?.mudou_nome_catalogo) {
          catalogoData.descricao = "";
        }
      } catch (error) {
        console.error("Erro ao verificar mudou_nome_catalogo:", error);
      }
    }

    catalogoData && setCatalogo(catalogoData);
    !outrosUsuariosData.erro && setOutrosUsuarios(outrosUsuariosData || []);
    intermediadores && setEmpresaIntermediadores(intermediadores);

    setLoading(false);
  };

  const salvarCatalogo = async () => {
    setLoading(true);

    // Filtrar descontos ativos para enviar ao backend (inativos são excluídos)
    const descontosAtivos = descontosProgressivos.filter(d => d.ativo);
    // Filtrar ajustes de categoria ativos para enviar ao backend (inativos são excluídos)
    const ajustesCategoriaAtivos = ajustesCategoria.filter(a => a.ativo);
    // Filtrar ajustes de variacao ativos para enviar ao backend (inativos são excluídos)
    const ajustesVariacaoAtivos = ajustesVariacao.filter(a => a.ativo);
    const identificacao = await catalogoApi.salvarCatalogo(catalogo, descontosAtivos, ajustesCategoriaAtivos, ajustesVariacaoAtivos);

    const isPrincipal = catalogo.principal === "true" || String(catalogo.principal) === "true";
    if (isPrincipal) {
      await configApi.marcar_mudou_nome_catalogo();
    }

    setLoading(false);

    return identificacao;
  };

  const removerCatalogo = async () => {
    setLoading(true);
    try {
      await catalogoApi.removerCatalogo(catalogo.id);
      Swal.fire({
        title: "Catálogo removido com sucesso!",
        icon: "success",
        confirmButtonText: "Ok",
        timer: 1250,
      });
      setLoading(false);
      navigate("/painel/catalogos");
    } catch (error) {
      Swal.fire({
        title: "Erro ao remover catálogo!",
        text: "Ocorreu um erro ao remover o catálogo, tente novamente.",
        icon: "error",
        confirmButtonText: "Ok",
      });
      setLoading(false);
    }
  };

  useEffect(() => {
    if (catalogo.descricao !== "") {
      setLoading(false);
    }
  }, [catalogo]);

  useEffect(() => {
    init();
  }, []);

  return (
    <CatalogoContext.Provider
      value={{
        init,
        listasDePrecos,
        empresaIntermediadores,
        catalogo,
        setCatalogo,
        setLoading,
        removerCatalogo,
        salvarCatalogo,
        loading,
        outrosUsuarios,
        setOutrosUsuarios,
        descontosProgressivos,
        setDescontosProgressivos,
        ajustesCategoria,
        setAjustesCategoria,
        ajustesVariacao,
        setAjustesVariacao,
        categorias,
      }}
    >
      {children}
    </CatalogoContext.Provider>
  );
};
